import {
  getPreStartQuestionList,
  QuestionAnswers,
  submitAnswersToPreStartQuestionList
} from '@api/pre-start-question-list';
import DynamicInputField from '@components/dynamic-input-field';
import { displayTime, isDateValid } from '@global/helpers/general';
import { Grid, Typography } from '@mui/material';
import { RootState } from '@redux/store';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

interface Validation {
  fn: string; // The validation function represented as a string
  message: string; // The validation message
}

interface Option {
  value: string; // The value of the option
  name: string; // The name of the option (defaults to value if not provided)
}

interface InputField {
  validation?: Validation; // Validation rules
  type?: string; // Field type
  inputType?: string; // Input field type
  label?: string; // The label for the input field
  question?: string; // The question for the input field
  options?: Option[]; // Optional field options, only for autocomplete type
  required?: boolean; // If the field is required
  placeholder?: string; // The placeholder for the input field
  fieldName: string; // The field's unique identifier (e.g., from `item._id`)
  value?: string | number | boolean; // The value of the field
  isEditable?: boolean; // Whether the field is editable,
  [key: string]: any; // Index signature for dynamic keys
}

interface QuestionsList {
  question?: string; // The question for the input field
  options?: Option[];
  _id: string; // The unique identifier for the input field
}

export default function usePreStartQuestionList() {
  const [preStartQuestionList, setPreStartQuestionList] = React.useState<Partial<InputField>>([]);
  const [input, setInput] = useState<{ [key: string]: any }>({});
  const [error, setError] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState<boolean>(false);

  const { txnInfo: form } = useSelector((s: RootState) => s.formData);
  const { searchedQuery } = useSelector((s: RootState) => s.misc);
  const preStartQuestionIsDisabled = preStartQuestionList?.some((x: InputField) => {
    if (
      x.required &&
      (x?.validation?.fn ? !eval(x?.validation?.fn)(input[x.fieldName]) : !input[x.fieldName])
    ) {
      return true;
    }
  });
  const updateField = (
    fieldName: string,
    value: any,
    indexOfRecordSet: string,
    deleteThisKey = false
  ) => {
    const temp1 = { ...input };
    if (!deleteThisKey) {
      temp1[fieldName] = typeof value === 'string' ? value : value.value;
    } else {
      delete temp1[fieldName];
    }
    setInput(temp1);
  };
  useEffect(() => {
    if (form?.questionnaire?.isRequired) {
      // Get pre-start question list and set it in the state
      setLoading(true);
      getPreStartQuestionList(searchedQuery.formId, searchedQuery.orgId).then((res) => {
        setLoading(false);
        const tempInput: InputField = { fieldName: '' };
        if (res) {
          const fields = res?.map((item: QuestionsList) => {
            const inputX: Partial<InputField> = {
              ...item,
              type: item.options ? 'autocomplete' : 'text',
              inputType: item.options ? 'autocomplete' : 'text',
              label: '',
              question: item?.question,
              options: item.options?.map((option) => {
                if (!option.name) {
                  return {
                    value: option.value,
                    name: option.value
                  };
                } else {
                  return option;
                }
              })
            };
            tempInput[item._id] = input[item._id] || '';
            return {
              validation: {
                fn: '(e) => { return e?.length > 0; }',
                message: `${inputX.question} should not be empty`
              },
              ...inputX,
              required: true,
              placeholder: '',
              fieldName: item._id,
              value: input[item._id],
              isEditable: true
            };
          });
          setInput(tempInput);
          setPreStartQuestionList(fields);
        }
      });
    }
  }, [form?.questionnaire?.isRequired]);

  const onPreStartAnswersSubmission = async () => {
    const obj: QuestionAnswers = [];
    const tempErr: any = {};
    const mixpanelData: { question: string, answer: string | number | boolean }[] = [];
    preStartQuestionList?.forEach((x: InputField) => {
      if (
        x.required &&
        x.fieldName &&
        (x.validation?.fn ? !eval(x?.validation?.fn)(input[x.fieldName]) : !input[x.fieldName])
      ) {
        tempErr[x.fieldName] = { message: x?.validation?.message };
        return false;
      }
      let val = input[x.fieldName];
      if (x?.inputType === 'date') {
        if (isDateValid(val)) {
          val = displayTime(val, x.format);
        }
      }
      obj.push({ questionId: x.fieldName, answer: val });
      mixpanelData.push({ question: x?.question ?? '', answer: val });
      return true;
    });

    setError(tempErr);
    if (tempErr.length > 0) {
      return false;
    }

    return submitAnswersToPreStartQuestionList(
      {
        txnId: searchedQuery.txnId,
        data: obj,
        orgId: searchedQuery.orgId
      },
      mixpanelData
    );
  };

  const preStartQuestionComponent: ReactNode =
    preStartQuestionList?.length > 0 ? (
      <Grid container direction={'column'}>
        <Grid container direction={'column'} sx={{ p: '1.1rem', rowGap: '2rem' }}>
          {preStartQuestionList?.map((preStartQuestion: InputField, i: number) => (
            <Grid item key={`preStartQuestion-${i}`}>
              <Typography variant="body2" sx={{ mb: 0, fontWeight: 600 }}>
                {preStartQuestion.question}
              </Typography>
              <DynamicInputField
                input={preStartQuestion}
                showAddOtherInAutocomplete
                fieldIdx={i}
                handleChange={({ name, value }: { name: string, value: any }) =>
                  updateField(name, value, preStartQuestion?._id)
                }
                value={input[preStartQuestion?.fieldName]}
                error={error[preStartQuestion?.fieldName]} //error bool
                helperText={error[preStartQuestion?.fieldName]?.message} //error bool
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    ) : null;

  return {
    preStartQuestionComponent,
    onPreStartAnswersSubmission,
    preStartQuestionIsDisabled,
    preStartQuestionListLength: preStartQuestionList.length > 0,
    loadingPreQuestionList: loading
  };
}
